/**
 * Campaign Wizard
 *
 * Multi-step wizard for GM Taste Interview.
 * Captures preferences that feed into AI directors and asset generators.
 *
 * Two paths:
 * - Quick Start: Name → Go (uses default profile)
 * - Full Setup: 8-12 questions, 2-4 minutes
 */

import { useState, useCallback } from 'react';
import { Icon } from './Icon';
import { useCampaignStore } from '../stores/campaignStore';
import { useAuthStore } from '../stores/authStore';
import {
  CampaignProfile,
  DEFAULT_CAMPAIGN_PROFILE,
  PLAYSETS,
  PlaysetId,
  applyPlayset,
  FunType,
  FUN_TYPE_LABELS,
  TONE_SLIDER_LABELS,
  MAGIC_LEVEL_LABELS,
  TECH_LEVEL_LABELS,
  MORAL_PHYSICS_LABELS,
  STRUCTURE_TYPE_LABELS,
  PACING_SLIDER_LABELS,
  GROUP_PATRON_LABELS,
  PREP_STYLE_LABELS,
  MagicLevel,
  TechLevel,
  MoralPhysics,
  StructureType,
  GroupPatron,
  PrepStyle,
  ALPHA_EMAILS,
  DEFAULT_SOLO_AI_CONFIG,
} from '@machina/core';
import { SoloCampaignSetup } from './SoloCampaignSetup';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useTierStore } from '../stores/tierStore';

interface CampaignWizardProps {
  onClose: () => void;
  onCreated?: (campaignId: string) => void;
  /** When provided, the wizard opens directly into Full Setup prefilled with
   * this playset's profile (used by the Worlds gallery "Start in this World"). */
  initialPlaysetId?: PlaysetId;
}

const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212, 175, 55, 0.3)';

type WizardMode = 'selecting' | 'quick' | 'full' | 'solo' | 'ai_dm';

// Full setup steps
const FULL_STEPS = [
  'basics',       // Step 1: Name + Description
  'funTypes',     // Step 2: Pick 2 fun types
  'tone',         // Step 3: Tone sliders
  'setting',      // Step 4: Setting config
  'moralPhysics', // Step 5: Moral physics
  'structure',    // Step 6: Structure type
  'pacing',       // Step 7: Pacing config
  'groupPatron',  // Step 8: Group patron
  'boundaries',   // Step 9: Content boundaries
  'prepStyle',    // Step 10: GM prep style
  'hook',         // Step 11: Session one image
  'review',       // Step 12: Review & create
] as const;

type FullStep = typeof FULL_STEPS[number];

export function CampaignWizard({ onClose, onCreated, initialPlaysetId }: CampaignWizardProps) {
  // Mode: selecting, quick, or full. When launched with a playset (e.g. from
  // the Worlds gallery), jump straight into Full Setup.
  const [mode, setMode] = useState<WizardMode>(initialPlaysetId ? 'full' : 'selecting');
  const [currentStep, setCurrentStep] = useState(0);

  // Form data
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [profile, setProfile] = useState<CampaignProfile>(() =>
    initialPlaysetId ? applyPlayset(initialPlaysetId) : { ...DEFAULT_CAMPAIGN_PROFILE }
  );

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createCampaign = useCampaignStore((s) => s.createCampaign);
  const campaigns = useCampaignStore((s) => s.campaigns);
  const user = useAuthStore((s) => s.user);
  const soloEnabled = useFeatureFlag('soloMode');
  const tierInfo = useTierStore((s) => s.tierInfo);

  // Handle playset selection
  const handlePlaysetSelect = useCallback((playsetId: PlaysetId) => {
    const newProfile = applyPlayset(playsetId);
    setProfile(newProfile);
    setMode('full');
  }, []);

  // Handle quick start
  const handleQuickStart = useCallback(() => {
    setProfile({ ...DEFAULT_CAMPAIGN_PROFILE });
    setMode('quick');
  }, []);

  // Handle full setup (no playset)
  const handleFullSetup = useCallback(() => {
    setProfile({ ...DEFAULT_CAMPAIGN_PROFILE });
    setMode('full');
  }, []);

  // Handle solo adventure
  const handleSoloSetup = useCallback(() => {
    setMode('solo');
  }, []);

  // Navigate steps
  const nextStep = useCallback(() => {
    if (mode === 'full' && currentStep < FULL_STEPS.length - 1) {
      setCurrentStep((s) => s + 1);
    }
  }, [mode, currentStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    } else {
      // Go back to mode selection
      setMode('selecting');
    }
  }, [currentStep]);

  // Update profile
  const updateProfile = useCallback(<K extends keyof CampaignProfile>(
    key: K,
    value: CampaignProfile[K]
  ) => {
    setProfile((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Submit campaign
  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Campaign name is required');
      return;
    }

    if (!user?.id) {
      setError('You must be logged in to create a campaign');
      return;
    }

    // Enforce campaign limit for free tier (alpha emails bypass)
    const isAlpha = ALPHA_EMAILS.includes(user.email?.toLowerCase() || '');
    if (!isAlpha && tierInfo.maxCampaigns !== null) {
      const ownedCampaigns = campaigns.filter((c) => c.ownerId === user.id);
      if (ownedCampaigns.length >= tierInfo.maxCampaigns) {
        setError(`Free tier limited to ${tierInfo.maxCampaigns} campaigns. Upgrade for unlimited.`);
        return;
      }
    }

    setIsSubmitting(true);
    setError(null);

    // Pass profile to createCampaign (stored in settings.profile)
    const campaignType = mode === 'ai_dm' ? 'ai_dm' as const : undefined;
    const campaign = await createCampaign(
      user.id,
      name.trim(),
      description.trim() || undefined,
      profile,
      campaignType,
      campaignType === 'ai_dm' ? DEFAULT_SOLO_AI_CONFIG : undefined,
    );

    setIsSubmitting(false);

    if (campaign) {
      onCreated?.(campaign.id);
      onClose();
    } else {
      setError('Failed to create campaign. Please try again.');
    }
  };

  // Determine if current step is complete
  const isStepComplete = useCallback((): boolean => {
    if (mode === 'quick' || mode === 'ai_dm') {
      return name.trim().length > 0;
    }

    const step = FULL_STEPS[currentStep];
    switch (step) {
      case 'basics':
        return name.trim().length > 0;
      case 'funTypes':
        return profile.funTypes.length === 2;
      default:
        return true; // Other steps have defaults
    }
  }, [mode, currentStep, name, profile]);

  // Progress percentage for full mode
  const progressPercent = mode === 'full'
    ? ((currentStep + 1) / FULL_STEPS.length) * 100
    : 100;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--color-background)',
          borderRadius: 'var(--radius-md)',
          border: `2px solid ${GOLD_DIM}`,
          padding: 0,
          width: mode === 'selecting' ? 560 : mode === 'solo' || mode === 'ai_dm' ? 560 : 480,
          maxWidth: '90vw',
          maxHeight: '85vh',
          overflow: 'hidden',
          boxShadow: `0 20px 60px rgba(0, 0, 0, 0.6), 0 0 40px rgba(212, 175, 55, 0.1)`,
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Progress Bar (Full Setup only) */}
        {mode === 'full' && (
          <div
            style={{
              height: 4,
              backgroundColor: 'var(--color-surface)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${progressPercent}%`,
                background: `linear-gradient(90deg, ${GOLD} 0%, #f0d878 100%)`,
                transition: 'width 0.3s ease',
              }}
            />
          </div>
        )}

        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 'var(--spacing-lg) var(--spacing-xl)',
            borderBottom: `1px solid ${GOLD_DIM}`,
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: 'var(--font-size-xl)',
                fontFamily: 'var(--font-display)',
                fontWeight: 400,
                color: GOLD,
                letterSpacing: '0.5px',
              }}
            >
              {mode === 'selecting' && 'Create New Campaign'}
              {mode === 'quick' && 'Quick Start'}
              {mode === 'solo' && 'Solo Adventure'}
              {mode === 'ai_dm' && 'Party + AI DM'}
              {mode === 'full' && getStepTitle(FULL_STEPS[currentStep])}
            </h2>
            {mode === 'full' && (
              <div
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                  marginTop: 4,
                }}
              >
                Step {currentStep + 1} of {FULL_STEPS.length}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-secondary)',
              cursor: 'pointer',
              padding: 'var(--spacing-xs)',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name="minus" size={18} />
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            padding: 'var(--spacing-xl)',
            flex: 1,
            overflowY: 'auto',
          }}
        >
          {/* Mode Selection */}
          {mode === 'selecting' && (
            <ModeSelection
              onQuickStart={handleQuickStart}
              onFullSetup={handleFullSetup}
              onSoloSetup={handleSoloSetup}
              onAiDmSetup={() => setMode('ai_dm')}
              onPlaysetSelect={handlePlaysetSelect}
              showSolo={soloEnabled}
            />
          )}

          {/* Quick Start: Just name + description */}
          {mode === 'quick' && (
            <BasicsStep
              name={name}
              setName={setName}
              description={description}
              setDescription={setDescription}
              isQuick
            />
          )}

          {/* Solo Adventure */}
          {mode === 'solo' && (
            <SoloCampaignSetup
              onComplete={() => {
                onClose();
              }}
              onCancel={() => setMode('selecting')}
              onCreated={onCreated}
            />
          )}

          {/* Party + AI DM: Same as quick start but creates ai_dm campaign */}
          {mode === 'ai_dm' && (
            <BasicsStep
              name={name}
              setName={setName}
              description={description}
              setDescription={setDescription}
              isQuick
            />
          )}

          {/* Full Setup Steps */}
          {mode === 'full' && (
            <>
              {FULL_STEPS[currentStep] === 'basics' && (
                <BasicsStep
                  name={name}
                  setName={setName}
                  description={description}
                  setDescription={setDescription}
                />
              )}
              {FULL_STEPS[currentStep] === 'funTypes' && (
                <FunTypesStep
                  selected={profile.funTypes}
                  onChange={(funTypes) => updateProfile('funTypes', funTypes)}
                />
              )}
              {FULL_STEPS[currentStep] === 'tone' && (
                <ToneStep
                  tone={profile.tone}
                  onChange={(tone) => updateProfile('tone', tone)}
                />
              )}
              {FULL_STEPS[currentStep] === 'setting' && (
                <SettingStep
                  setting={profile.setting}
                  onChange={(setting) => updateProfile('setting', setting)}
                />
              )}
              {FULL_STEPS[currentStep] === 'moralPhysics' && (
                <MoralPhysicsStep
                  selected={profile.moralPhysics}
                  onChange={(mp) => updateProfile('moralPhysics', mp)}
                />
              )}
              {FULL_STEPS[currentStep] === 'structure' && (
                <StructureStep
                  selected={profile.structure}
                  onChange={(s) => updateProfile('structure', s)}
                />
              )}
              {FULL_STEPS[currentStep] === 'pacing' && (
                <PacingStep
                  pacing={profile.pacing}
                  onChange={(p) => updateProfile('pacing', p)}
                />
              )}
              {FULL_STEPS[currentStep] === 'groupPatron' && (
                <GroupPatronStep
                  groupPatron={profile.groupPatron}
                  onChange={(gp) => updateProfile('groupPatron', gp)}
                />
              )}
              {FULL_STEPS[currentStep] === 'boundaries' && (
                <BoundariesStep
                  boundaries={profile.boundaries}
                  onChange={(b) => updateProfile('boundaries', b)}
                />
              )}
              {FULL_STEPS[currentStep] === 'prepStyle' && (
                <PrepStyleStep
                  selected={profile.prepStyle}
                  onChange={(ps) => updateProfile('prepStyle', ps)}
                />
              )}
              {FULL_STEPS[currentStep] === 'hook' && (
                <HookStep
                  hook={profile.sessionOneImage}
                  onChange={(h) => updateProfile('sessionOneImage', h)}
                />
              )}
              {FULL_STEPS[currentStep] === 'review' && (
                <ReviewStep
                  name={name}
                  description={description}
                  profile={profile}
                />
              )}
            </>
          )}

          {/* Error */}
          {error && (
            <div
              style={{
                marginTop: 'var(--spacing-md)',
                padding: 'var(--spacing-sm) var(--spacing-md)',
                backgroundColor: 'rgba(220, 20, 60, 0.15)',
                border: '1px solid rgba(220, 20, 60, 0.3)',
                borderRadius: 'var(--radius-sm)',
                color: '#dc143c',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              {error}
            </div>
          )}
        </div>

        {/* Footer Navigation */}
        {mode !== 'selecting' && mode !== 'solo' && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: 'var(--spacing-lg) var(--spacing-xl)',
              borderTop: `1px solid ${GOLD_DIM}`,
              backgroundColor: 'var(--color-surface)',
            }}
          >
            <button
              type="button"
              onClick={prevStep}
              disabled={isSubmitting}
              style={{
                padding: 'var(--spacing-sm) var(--spacing-lg)',
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'var(--font-display)',
                backgroundColor: 'transparent',
                border: `1px solid var(--color-border)`,
                borderRadius: 'var(--radius-sm)',
                color: 'var(--color-text-secondary)',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-xs)',
              }}
            >
              <Icon name="chevron-up" size={14} style={{ transform: 'rotate(-90deg)' }} />
              Back
            </button>

            {/* Next or Create button */}
            {(mode === 'quick' || mode === 'ai_dm' || (mode === 'full' && currentStep === FULL_STEPS.length - 1)) ? (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || !isStepComplete()}
                style={{
                  padding: 'var(--spacing-sm) var(--spacing-xl)',
                  fontSize: 'var(--font-size-sm)',
                  fontFamily: 'var(--font-display)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  background: `linear-gradient(180deg, ${GOLD} 0%, #a08030 50%, #8b6914 100%)`,
                  border: `2px solid ${GOLD}`,
                  borderRadius: 'var(--radius-sm)',
                  color: '#1a1410',
                  cursor: isSubmitting || !isStepComplete() ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting || !isStepComplete() ? 0.6 : 1,
                  boxShadow: `0 2px 8px rgba(212, 175, 55, 0.3)`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-sm)',
                }}
              >
                {isSubmitting ? (
                  <>
                    <Icon name="slow" size={14} />
                    Creating...
                  </>
                ) : (
                  <>
                    <Icon name="scroll" size={14} />
                    Create Campaign
                  </>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={nextStep}
                disabled={!isStepComplete()}
                style={{
                  padding: 'var(--spacing-sm) var(--spacing-lg)',
                  fontSize: 'var(--font-size-sm)',
                  fontFamily: 'var(--font-display)',
                  fontWeight: 500,
                  background: isStepComplete()
                    ? `linear-gradient(180deg, ${GOLD} 0%, #a08030 100%)`
                    : 'var(--color-surface)',
                  border: `1px solid ${isStepComplete() ? GOLD : 'var(--color-border)'}`,
                  borderRadius: 'var(--radius-sm)',
                  color: isStepComplete() ? '#1a1410' : 'var(--color-text-muted)',
                  cursor: isStepComplete() ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-xs)',
                }}
              >
                Next
                <Icon name="chevron-up" size={14} style={{ transform: 'rotate(90deg)' }} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// STEP TITLE HELPER
// =============================================================================

function getStepTitle(step: FullStep): string {
  switch (step) {
    case 'basics':
      return 'Campaign Basics';
    case 'funTypes':
      return 'What\'s Fun?';
    case 'tone':
      return 'Set the Tone';
    case 'setting':
      return 'World Setting';
    case 'moralPhysics':
      return 'Moral Compass';
    case 'structure':
      return 'Campaign Structure';
    case 'pacing':
      return 'Pacing & Agency';
    case 'groupPatron':
      return 'Party Bond';
    case 'boundaries':
      return 'Content Boundaries';
    case 'prepStyle':
      return 'GM Prep Style';
    case 'hook':
      return 'The Hook';
    case 'review':
      return 'Review & Create';
    default:
      return 'Campaign Setup';
  }
}

// =============================================================================
// MODE SELECTION
// =============================================================================

interface ModeSelectionProps {
  onQuickStart: () => void;
  onFullSetup: () => void;
  onSoloSetup: () => void;
  onAiDmSetup: () => void;
  onPlaysetSelect: (id: PlaysetId) => void;
  showSolo: boolean;
}

function ModeSelection({ onQuickStart, onFullSetup, onSoloSetup, onAiDmSetup, onPlaysetSelect, showSolo }: ModeSelectionProps) {
  return (
    <div>
      {/* Three mode buttons */}
      <div style={{ display: 'flex', gap: 'var(--spacing-lg)', marginBottom: 'var(--spacing-xl)' }}>
        <button
          onClick={onFullSetup}
          style={{
            flex: 1,
            padding: 'var(--spacing-lg)',
            backgroundColor: 'var(--color-surface)',
            border: `2px solid ${GOLD_DIM}`,
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            textAlign: 'left',
            transition: 'border-color 0.2s, background-color 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = GOLD;
            e.currentTarget.style.backgroundColor = 'rgba(212, 175, 55, 0.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = GOLD_DIM;
            e.currentTarget.style.backgroundColor = 'var(--color-surface)';
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-sm)' }}>
            <Icon name="sparkle" size={20} color={GOLD} />
            <span style={{ fontSize: 'var(--font-size-lg)', fontFamily: 'var(--font-display)', color: GOLD }}>
              Full Setup
            </span>
          </div>
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
            2-4 minute wizard for a tailored experience
          </div>
        </button>

        <button
          onClick={onQuickStart}
          style={{
            flex: 1,
            padding: 'var(--spacing-lg)',
            backgroundColor: 'var(--color-surface)',
            border: `2px solid var(--color-border)`,
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            textAlign: 'left',
            transition: 'border-color 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = 'var(--color-text-secondary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'var(--color-border)';
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-sm)' }}>
            <Icon name="lightning" size={20} color="var(--color-text-secondary)" />
            <span style={{ fontSize: 'var(--font-size-lg)', fontFamily: 'var(--font-display)', color: 'var(--color-text)' }}>
              Quick Start
            </span>
          </div>
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
            Just name it and go with defaults
          </div>
        </button>
      </div>

      {/* Solo Adventure button - full width, distinct style (feature-flagged) */}
      {showSolo && <button
        onClick={onSoloSetup}
        style={{
          width: '100%',
          padding: 'var(--spacing-lg)',
          backgroundColor: 'rgba(147, 51, 234, 0.05)',
          border: '2px solid rgba(147, 51, 234, 0.3)',
          borderRadius: 'var(--radius-md)',
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'border-color 0.2s, background-color 0.2s',
          marginBottom: 'var(--spacing-xl)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#9333ea';
          e.currentTarget.style.backgroundColor = 'rgba(147, 51, 234, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'rgba(147, 51, 234, 0.3)';
          e.currentTarget.style.backgroundColor = 'rgba(147, 51, 234, 0.05)';
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-sm)' }}>
          <Icon name="sparkle" size={20} color="#9333ea" />
          <span style={{ fontSize: 'var(--font-size-lg)', fontFamily: 'var(--font-display)', color: '#9333ea' }}>
            Solo Adventure
          </span>
          <span style={{
            fontSize: 'var(--font-size-xs)',
            color: '#9333ea',
            border: '1px solid rgba(147, 51, 234, 0.3)',
            borderRadius: 'var(--radius-sm)',
            padding: '1px 6px',
            fontFamily: 'var(--font-body)',
          }}>
            AI DM
          </span>
        </div>
        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
          Play solo with an AI Dungeon Master that narrates, creates encounters, and runs NPCs
        </div>
      </button>}

      {/* Multiplayer + AI DM button */}
      {showSolo && <button
        onClick={onAiDmSetup}
        style={{
          width: '100%',
          padding: 'var(--spacing-lg)',
          backgroundColor: 'rgba(212, 175, 55, 0.05)',
          border: '2px solid rgba(212, 175, 55, 0.3)',
          borderRadius: 'var(--radius-md)',
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'border-color 0.2s, background-color 0.2s',
          marginBottom: 'var(--spacing-xl)',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#d4af37';
          e.currentTarget.style.backgroundColor = 'rgba(212, 175, 55, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.3)';
          e.currentTarget.style.backgroundColor = 'rgba(212, 175, 55, 0.05)';
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-sm)' }}>
          <Icon name="users" size={20} color="#d4af37" />
          <span style={{ fontSize: 'var(--font-size-lg)', fontFamily: 'var(--font-display)', color: '#d4af37' }}>
            Party + AI DM
          </span>
          <span style={{
            fontSize: 'var(--font-size-xs)',
            color: '#d4af37',
            border: '1px solid rgba(212, 175, 55, 0.3)',
            borderRadius: 'var(--radius-sm)',
            padding: '1px 6px',
            fontFamily: 'var(--font-body)',
          }}>
            NEW
          </span>
        </div>
        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
          Play with friends — AI runs the game as Dungeon Master for the whole party
        </div>
      </button>}

      {/* Playset shortcuts */}
      <div>
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            fontFamily: 'var(--font-display)',
            color: 'var(--color-text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            marginBottom: 'var(--spacing-md)',
          }}
        >
          Or start with a playset:
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-sm)' }}>
          {(Object.keys(PLAYSETS) as PlaysetId[]).map((id) => {
            const playset = PLAYSETS[id];
            return (
              <button
                key={id}
                onClick={() => onPlaysetSelect(id)}
                style={{
                  padding: 'var(--spacing-md)',
                  backgroundColor: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'border-color 0.2s, background-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = GOLD_DIM;
                  e.currentTarget.style.backgroundColor = 'rgba(212, 175, 55, 0.03)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-border)';
                  e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                }}
              >
                <div style={{ fontSize: 'var(--font-size-md)', color: 'var(--color-text)', marginBottom: 2 }}>
                  {playset.name}
                </div>
                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                  {playset.tagline}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// STEP: BASICS (Name + Description)
// =============================================================================

interface BasicsStepProps {
  name: string;
  setName: (n: string) => void;
  description: string;
  setDescription: (d: string) => void;
  isQuick?: boolean;
}

function BasicsStep({ name, setName, description, setDescription, isQuick }: BasicsStepProps) {
  return (
    <div>
      {isQuick && (
        <div
          style={{
            marginBottom: 'var(--spacing-lg)',
            padding: 'var(--spacing-md)',
            backgroundColor: 'rgba(212, 175, 55, 0.1)',
            borderRadius: 'var(--radius-sm)',
            border: `1px solid ${GOLD_DIM}`,
          }}
        >
          <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
            You can always customize your campaign profile later from the settings.
          </div>
        </div>
      )}

      {/* Campaign Name */}
      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <label
          htmlFor="campaign-name"
          style={{
            display: 'block',
            marginBottom: 'var(--spacing-xs)',
            fontSize: 'var(--font-size-sm)',
            fontFamily: 'var(--font-display)',
            color: 'var(--color-text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '1px',
          }}
        >
          Campaign Name *
        </label>
        <input
          id="campaign-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Curse of Strahd"
          autoFocus
          style={{
            width: '100%',
            padding: 'var(--spacing-sm) var(--spacing-md)',
            fontSize: 'var(--font-size-md)',
            fontFamily: 'var(--font-body)',
            backgroundColor: 'var(--color-surface)',
            border: `1px solid var(--color-border)`,
            borderRadius: 'var(--radius-sm)',
            color: 'var(--color-text)',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Description */}
      <div>
        <label
          htmlFor="campaign-description"
          style={{
            display: 'block',
            marginBottom: 'var(--spacing-xs)',
            fontSize: 'var(--font-size-sm)',
            fontFamily: 'var(--font-display)',
            color: 'var(--color-text-secondary)',
            textTransform: 'uppercase',
            letterSpacing: '1px',
          }}
        >
          Description
        </label>
        <textarea
          id="campaign-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="A brief description for your players..."
          rows={3}
          style={{
            width: '100%',
            padding: 'var(--spacing-sm) var(--spacing-md)',
            fontSize: 'var(--font-size-md)',
            fontFamily: 'var(--font-body)',
            backgroundColor: 'var(--color-surface)',
            border: `1px solid var(--color-border)`,
            borderRadius: 'var(--radius-sm)',
            color: 'var(--color-text)',
            outline: 'none',
            resize: 'vertical',
            minHeight: 80,
            boxSizing: 'border-box',
          }}
        />
      </div>
    </div>
  );
}

// =============================================================================
// STEP: FUN TYPES (Pick 2)
// =============================================================================

interface FunTypesStepProps {
  selected: [FunType, FunType];
  onChange: (funTypes: [FunType, FunType]) => void;
}

function FunTypesStep({ selected, onChange }: FunTypesStepProps) {
  const allTypes = Object.keys(FUN_TYPE_LABELS) as FunType[];

  const handleToggle = (type: FunType) => {
    if (selected.includes(type)) {
      // Remove it (but keep array valid)
      const remaining = selected.filter((t) => t !== type);
      if (remaining.length === 1) {
        onChange([remaining[0], remaining[0]]); // Duplicate placeholder
      }
    } else {
      // Add it (replace oldest)
      onChange([selected[1], type]);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 'var(--spacing-lg)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
        Pick the <strong style={{ color: GOLD }}>two</strong> types of fun that matter most to your table:
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-sm)' }}>
        {allTypes.map((type) => {
          const isSelected = selected.includes(type);
          const { label, description } = FUN_TYPE_LABELS[type];
          return (
            <button
              key={type}
              onClick={() => handleToggle(type)}
              style={{
                padding: 'var(--spacing-md)',
                backgroundColor: isSelected ? 'rgba(212, 175, 55, 0.15)' : 'var(--color-surface)',
                border: `2px solid ${isSelected ? GOLD : 'var(--color-border)'}`,
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.2s',
              }}
            >
              <div style={{ fontSize: 'var(--font-size-md)', color: isSelected ? GOLD : 'var(--color-text)', fontWeight: isSelected ? 600 : 400 }}>
                {label}
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>
                {description}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// STEP: TONE (3 Sliders)
// =============================================================================

interface ToneStepProps {
  tone: CampaignProfile['tone'];
  onChange: (tone: CampaignProfile['tone']) => void;
}

function ToneStep({ tone, onChange }: ToneStepProps) {
  return (
    <div>
      <div style={{ marginBottom: 'var(--spacing-lg)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
        Adjust these sliders to set the overall tone of your campaign:
      </div>

      <ToneSlider
        label={TONE_SLIDER_LABELS.noblebright_grimdark.description}
        leftLabel={TONE_SLIDER_LABELS.noblebright_grimdark.left}
        rightLabel={TONE_SLIDER_LABELS.noblebright_grimdark.right}
        value={tone.noblebright_grimdark}
        onChange={(v) => onChange({ ...tone, noblebright_grimdark: v })}
      />

      <ToneSlider
        label={TONE_SLIDER_LABELS.sincere_gonzo.description}
        leftLabel={TONE_SLIDER_LABELS.sincere_gonzo.left}
        rightLabel={TONE_SLIDER_LABELS.sincere_gonzo.right}
        value={tone.sincere_gonzo}
        onChange={(v) => onChange({ ...tone, sincere_gonzo: v })}
      />

      <ToneSlider
        label={TONE_SLIDER_LABELS.heroic_lethal.description}
        leftLabel={TONE_SLIDER_LABELS.heroic_lethal.left}
        rightLabel={TONE_SLIDER_LABELS.heroic_lethal.right}
        value={tone.heroic_lethal}
        onChange={(v) => onChange({ ...tone, heroic_lethal: v })}
      />
    </div>
  );
}

interface ToneSliderProps {
  label: string;
  leftLabel: string;
  rightLabel: string;
  value: number;
  onChange: (v: number) => void;
}

function ToneSlider({ label, leftLabel, rightLabel, value, onChange }: ToneSliderProps) {
  return (
    <div style={{ marginBottom: 'var(--spacing-xl)' }}>
      <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-sm)' }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', minWidth: 80, textAlign: 'right' }}>
          {leftLabel}
        </span>
        <input
          type="range"
          min={0}
          max={100}
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value, 10))}
          style={{
            flex: 1,
            accentColor: GOLD,
            height: 6,
          }}
        />
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', minWidth: 80 }}>
          {rightLabel}
        </span>
      </div>
    </div>
  );
}

// =============================================================================
// STEP: SETTING
// =============================================================================

interface SettingStepProps {
  setting: CampaignProfile['setting'];
  onChange: (s: CampaignProfile['setting']) => void;
}

function SettingStep({ setting, onChange }: SettingStepProps) {
  return (
    <div>
      {/* Mythic vs Grounded slider */}
      <ToneSlider
        label={'How "mythic" is the world?'}
        leftLabel="Mythic"
        rightLabel="Grounded"
        value={setting.mythic_grounded}
        onChange={(v) => onChange({ ...setting, mythic_grounded: v })}
      />

      {/* Magic Level */}
      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-sm)' }}>
          Magic Level
        </div>
        <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
          {(['low', 'medium', 'wild'] as MagicLevel[]).map((level) => (
            <button
              key={level}
              onClick={() => onChange({ ...setting, magic_level: level })}
              style={{
                flex: 1,
                padding: 'var(--spacing-sm)',
                backgroundColor: setting.magic_level === level ? 'rgba(212, 175, 55, 0.15)' : 'var(--color-surface)',
                border: `1px solid ${setting.magic_level === level ? GOLD : 'var(--color-border)'}`,
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                color: setting.magic_level === level ? GOLD : 'var(--color-text)',
              }}
            >
              {MAGIC_LEVEL_LABELS[level]}
            </button>
          ))}
        </div>
      </div>

      {/* Tech Level */}
      <div>
        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-sm)' }}>
          Tech Level
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
          {(['bronze', 'medieval', 'renaissance', 'industrial', 'steampunk'] as TechLevel[]).map((level) => (
            <button
              key={level}
              onClick={() => onChange({ ...setting, tech_level: level })}
              style={{
                padding: 'var(--spacing-sm)',
                backgroundColor: setting.tech_level === level ? 'rgba(212, 175, 55, 0.15)' : 'var(--color-surface)',
                border: `1px solid ${setting.tech_level === level ? GOLD : 'var(--color-border)'}`,
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm)',
                color: setting.tech_level === level ? GOLD : 'var(--color-text)',
                textAlign: 'left',
              }}
            >
              {TECH_LEVEL_LABELS[level]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// STEP: MORAL PHYSICS
// =============================================================================

interface MoralPhysicsStepProps {
  selected: MoralPhysics;
  onChange: (mp: MoralPhysics) => void;
}

function MoralPhysicsStep({ selected, onChange }: MoralPhysicsStepProps) {
  const options = Object.keys(MORAL_PHYSICS_LABELS) as MoralPhysics[];

  return (
    <div>
      <div style={{ marginBottom: 'var(--spacing-lg)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
        How does morality work in your world?
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
        {options.map((mp) => {
          const { label, description } = MORAL_PHYSICS_LABELS[mp];
          const isSelected = selected === mp;
          return (
            <button
              key={mp}
              onClick={() => onChange(mp)}
              style={{
                padding: 'var(--spacing-md)',
                backgroundColor: isSelected ? 'rgba(212, 175, 55, 0.15)' : 'var(--color-surface)',
                border: `2px solid ${isSelected ? GOLD : 'var(--color-border)'}`,
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ fontSize: 'var(--font-size-md)', color: isSelected ? GOLD : 'var(--color-text)', fontWeight: isSelected ? 600 : 400 }}>
                {label}
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>
                {description}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// STEP: STRUCTURE
// =============================================================================

interface StructureStepProps {
  selected: StructureType;
  onChange: (s: StructureType) => void;
}

function StructureStep({ selected, onChange }: StructureStepProps) {
  const options = Object.keys(STRUCTURE_TYPE_LABELS) as StructureType[];

  return (
    <div>
      <div style={{ marginBottom: 'var(--spacing-lg)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
        How is the campaign structured?
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
        {options.map((s) => {
          const { label, description } = STRUCTURE_TYPE_LABELS[s];
          const isSelected = selected === s;
          return (
            <button
              key={s}
              onClick={() => onChange(s)}
              style={{
                padding: 'var(--spacing-md)',
                backgroundColor: isSelected ? 'rgba(212, 175, 55, 0.15)' : 'var(--color-surface)',
                border: `2px solid ${isSelected ? GOLD : 'var(--color-border)'}`,
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ fontSize: 'var(--font-size-md)', color: isSelected ? GOLD : 'var(--color-text)', fontWeight: isSelected ? 600 : 400 }}>
                {label}
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>
                {description}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// STEP: PACING
// =============================================================================

interface PacingStepProps {
  pacing: CampaignProfile['pacing'];
  onChange: (p: CampaignProfile['pacing']) => void;
}

function PacingStep({ pacing, onChange }: PacingStepProps) {
  return (
    <div>
      <div style={{ marginBottom: 'var(--spacing-lg)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
        Set your preferences for combat vs roleplay and player agency:
      </div>

      <ToneSlider
        label={PACING_SLIDER_LABELS.combat_rp_ratio.description}
        leftLabel={PACING_SLIDER_LABELS.combat_rp_ratio.left}
        rightLabel={PACING_SLIDER_LABELS.combat_rp_ratio.right}
        value={pacing.combat_rp_ratio}
        onChange={(v) => onChange({ ...pacing, combat_rp_ratio: v })}
      />

      <ToneSlider
        label={PACING_SLIDER_LABELS.player_agency.description}
        leftLabel={PACING_SLIDER_LABELS.player_agency.left}
        rightLabel={PACING_SLIDER_LABELS.player_agency.right}
        value={pacing.player_agency}
        onChange={(v) => onChange({ ...pacing, player_agency: v })}
      />
    </div>
  );
}

// =============================================================================
// STEP: GROUP PATRON
// =============================================================================

interface GroupPatronStepProps {
  groupPatron: CampaignProfile['groupPatron'];
  onChange: (gp: CampaignProfile['groupPatron']) => void;
}

function GroupPatronStep({ groupPatron, onChange }: GroupPatronStepProps) {
  const options = Object.keys(GROUP_PATRON_LABELS) as GroupPatron[];

  return (
    <div>
      <div style={{ marginBottom: 'var(--spacing-lg)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
        What binds the party together?
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-sm)' }}>
        {options.map((gp) => {
          const { label, description } = GROUP_PATRON_LABELS[gp];
          const isSelected = groupPatron.type === gp;
          return (
            <button
              key={gp}
              onClick={() => onChange({ type: gp, customName: gp === 'custom' ? groupPatron.customName : undefined })}
              style={{
                padding: 'var(--spacing-md)',
                backgroundColor: isSelected ? 'rgba(212, 175, 55, 0.15)' : 'var(--color-surface)',
                border: `2px solid ${isSelected ? GOLD : 'var(--color-border)'}`,
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ fontSize: 'var(--font-size-sm)', color: isSelected ? GOLD : 'var(--color-text)', fontWeight: isSelected ? 600 : 400 }}>
                {label}
              </div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>
                {description}
              </div>
            </button>
          );
        })}
      </div>

      {/* Custom name input */}
      {groupPatron.type === 'custom' && (
        <div style={{ marginTop: 'var(--spacing-md)' }}>
          <input
            type="text"
            value={groupPatron.customName || ''}
            onChange={(e) => onChange({ ...groupPatron, customName: e.target.value })}
            placeholder="Name your group bond..."
            style={{
              width: '100%',
              padding: 'var(--spacing-sm) var(--spacing-md)',
              fontSize: 'var(--font-size-md)',
              backgroundColor: 'var(--color-surface)',
              border: `1px solid var(--color-border)`,
              borderRadius: 'var(--radius-sm)',
              color: 'var(--color-text)',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
      )}
    </div>
  );
}

// =============================================================================
// STEP: BOUNDARIES
// =============================================================================

interface BoundariesStepProps {
  boundaries: CampaignProfile['boundaries'];
  onChange: (b: CampaignProfile['boundaries']) => void;
}

function BoundariesStep({ boundaries, onChange }: BoundariesStepProps) {
  return (
    <div>
      <div style={{ marginBottom: 'var(--spacing-lg)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
        Set content boundaries for your table. You can skip this and discuss with your players later.
      </div>

      {/* Safety tools toggle */}
      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={boundaries.use_safety_tools}
            onChange={(e) => onChange({ ...boundaries, use_safety_tools: e.target.checked })}
            style={{ accentColor: GOLD }}
          />
          <span style={{ color: 'var(--color-text)' }}>Use safety tools (X-card, Lines & Veils)</span>
        </label>
      </div>

      {/* Lines & Veils are advanced - just show placeholder for now */}
      <div
        style={{
          padding: 'var(--spacing-md)',
          backgroundColor: 'var(--color-surface)',
          borderRadius: 'var(--radius-sm)',
          border: '1px dashed var(--color-border)',
          color: 'var(--color-text-muted)',
          fontSize: 'var(--font-size-sm)',
        }}
      >
        <Icon name="shield" size={16} style={{ marginRight: 'var(--spacing-sm)', verticalAlign: 'middle' }} />
        Detailed Lines & Veils configuration coming soon. For now, discuss boundaries with your players at session zero.
      </div>
    </div>
  );
}

// =============================================================================
// STEP: PREP STYLE
// =============================================================================

interface PrepStyleStepProps {
  selected: PrepStyle;
  onChange: (ps: PrepStyle) => void;
}

function PrepStyleStep({ selected, onChange }: PrepStyleStepProps) {
  const options = Object.keys(PREP_STYLE_LABELS) as PrepStyle[];

  return (
    <div>
      <div style={{ marginBottom: 'var(--spacing-lg)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
        How do you like to prep? This affects what the AI generates for you.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
        {options.map((ps) => {
          const { label, description } = PREP_STYLE_LABELS[ps];
          const isSelected = selected === ps;
          return (
            <button
              key={ps}
              onClick={() => onChange(ps)}
              style={{
                padding: 'var(--spacing-lg)',
                backgroundColor: isSelected ? 'rgba(212, 175, 55, 0.15)' : 'var(--color-surface)',
                border: `2px solid ${isSelected ? GOLD : 'var(--color-border)'}`,
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ fontSize: 'var(--font-size-lg)', color: isSelected ? GOLD : 'var(--color-text)', fontWeight: isSelected ? 600 : 400 }}>
                {label}
              </div>
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', marginTop: 4 }}>
                {description}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// STEP: HOOK (Session One Image)
// =============================================================================

interface HookStepProps {
  hook: string;
  onChange: (h: string) => void;
}

function HookStep({ hook, onChange }: HookStepProps) {
  return (
    <div>
      <div style={{ marginBottom: 'var(--spacing-lg)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
        Describe the opening scene of your first session in 1-2 sentences. This sets the tone and gives players a starting image.
      </div>

      <textarea
        value={hook}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g., 'The party meets in a tavern as rain hammers the windows. A cloaked figure approaches with an urgent request...'"
        rows={4}
        style={{
          width: '100%',
          padding: 'var(--spacing-md)',
          fontSize: 'var(--font-size-md)',
          fontFamily: 'var(--font-body)',
          backgroundColor: 'var(--color-surface)',
          border: `1px solid var(--color-border)`,
          borderRadius: 'var(--radius-sm)',
          color: 'var(--color-text)',
          outline: 'none',
          resize: 'vertical',
          minHeight: 100,
          boxSizing: 'border-box',
        }}
      />

      <div style={{ marginTop: 'var(--spacing-sm)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
        This is optional - you can leave it blank and add it later.
      </div>
    </div>
  );
}

// =============================================================================
// STEP: REVIEW
// =============================================================================

interface ReviewStepProps {
  name: string;
  description: string;
  profile: CampaignProfile;
}

function ReviewStep({ name, description, profile }: ReviewStepProps) {
  const funTypeLabels = profile.funTypes.map((ft) => FUN_TYPE_LABELS[ft].label);

  return (
    <div>
      <div style={{ marginBottom: 'var(--spacing-lg)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
        Review your campaign settings before creating:
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
        <ReviewRow label="Name" value={name} />
        {description && <ReviewRow label="Description" value={description} />}
        {profile.playset && <ReviewRow label="Playset" value={PLAYSETS[profile.playset as PlaysetId]?.name || profile.playset} />}
        <ReviewRow label="Fun Types" value={funTypeLabels.join(', ')} />
        <ReviewRow label="Moral Physics" value={MORAL_PHYSICS_LABELS[profile.moralPhysics].label} />
        <ReviewRow label="Structure" value={STRUCTURE_TYPE_LABELS[profile.structure].label} />
        <ReviewRow label="Group Bond" value={GROUP_PATRON_LABELS[profile.groupPatron.type].label} />
        <ReviewRow label="Prep Style" value={PREP_STYLE_LABELS[profile.prepStyle].label} />
        {profile.sessionOneImage && <ReviewRow label="Opening Hook" value={profile.sessionOneImage} />}
      </div>
    </div>
  );
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: 'var(--spacing-sm) 0',
        borderBottom: '1px solid var(--color-border)',
      }}
    >
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', minWidth: 100 }}>
        {label}
      </span>
      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text)', textAlign: 'right', flex: 1 }}>
        {value}
      </span>
    </div>
  );
}
